import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import GuideButton from '../../common/guide/GuideButton';
import InventoryProductSelectionModal from '../../accounting/inventory/components/InventoryProductSelectionModal';
import RecordHistoryModal from '../../common/RecordHistoryModal';
import ProductionChildOrdersPanel from '../components/orders/ProductionChildOrdersPanel';
import ProductionCostSummary from '../components/orders/ProductionCostSummary';
import ProductionHeaderForm from '../components/orders/ProductionHeaderForm';
import ProductionInputsTable from '../components/orders/ProductionInputsTable';
import ProductionLotsPanel from '../components/orders/ProductionLotsPanel';
import ProductionMovementsTable from '../components/orders/ProductionMovementsTable';
import ProductionOrderDetailCard from '../components/orders/ProductionOrderDetailCard';
import ProductionOutputsTable from '../components/orders/ProductionOutputsTable';
import CompleteOperationsDialog from '../components/operations/CompleteOperationsDialog';
import OrderStepsPanel from '../components/operations/OrderStepsPanel';
import { useProductionNav } from '../context/ProductionNavContext';
import { useProductionPermissions } from '../context/ProductionPermissionsContext';
import CancelProductionDialog from '../dialogs/CancelProductionDialog';
import CompleteProductionDialog from '../dialogs/CompleteProductionDialog';
import ShortageDialog from '../dialogs/ShortageDialog';
import { useProductionOrderForm } from '../hooks/useProductionOrderForm';
import { rescaleRange, scaleExpectedRange } from '../utils/outputRange';

interface ProductionOrderEditorProps {
  orderId?: string;
}

const ProductionOrderEditor: React.FC<ProductionOrderEditorProps> = ({ orderId }) => {
  const params = useParams<{ id?: string }>();
  const id = orderId ?? params.id;
  const { t } = useTranslation('production');
  const { isModuleEnabled } = useAuth();
  const opsEnabled = isModuleEnabled('production_ops');
  const lotsEnabled = isModuleEnabled('lot_tracking');
  const nav = useProductionNav();
  const perms = useProductionPermissions();
  const form = useProductionOrderForm(id);
  const { loadMovements } = form;
  const [tab, setTab] = useState('inputs');
  const [opsCanComplete, setOpsCanComplete] = useState(false);
  const [opsProduced, setOpsProduced] = useState(0);
  const [opsCompleteOpen, setOpsCompleteOpen] = useState(false);

  useEffect(() => {
    const status = form.order?.status;
    if (opsEnabled && form.order?.hasRouting && (status === 'RELEASED' || status === 'IN_PROGRESS')) {
      setTab('operations');
    } else {
      setTab('inputs');
    }
  }, [id, form.order?.status, form.order?.hasRouting, opsEnabled]);

  useEffect(() => {
    if (tab === 'movements') loadMovements();
  }, [tab, loadMovements]);

  if (form.moduleDisabled) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{t('errors.PRODUCTION_MODULE_DISABLED')}</Alert>
      </Box>
    );
  }

  if (id && !form.order) {
    return (
      <Box sx={{ p: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={form.requestLeave}>{t('orders.title')}</Button>
        {form.loading ? <CircularProgress sx={{ display: 'block', mt: 2 }} /> : null}
        {form.banner ? (
          <Alert severity={form.banner.severity} sx={{ mt: 2 }} onClose={form.clearBanner}>
            {form.banner.message}
            {form.banner.detail ? <Typography variant="body2">{form.banner.detail}</Typography> : null}
          </Alert>
        ) : null}
      </Box>
    );
  }

  const children = form.order?.children ?? [];
  // Değişken çıktı: beklenen aralık reçete partisinden ya da fişin planlananından oranlanır.
  const completePlanned = form.plannedQuantity ?? form.order?.plannedQuantity ?? 0;
  const completeRange = scaleExpectedRange(
    form.product?.expectedOutputMin,
    form.product?.expectedOutputMax,
    form.product?.recipeOutputQuantity,
    completePlanned,
  ) ?? rescaleRange(
    form.order && (form.order.expectedProducedMin != null || form.order.expectedProducedMax != null)
      ? { min: form.order.expectedProducedMin ?? null, max: form.order.expectedProducedMax ?? null }
      : null,
    form.order?.plannedQuantity ?? 0,
    completePlanned,
  );
  const showChildren = children.length > 0;
  const showMovements = form.order?.status === 'COMPLETED'
    || form.order?.status === 'CANCELLED'
    || form.order?.status === 'IN_PROGRESS';
  const routingOn = opsEnabled && form.useRouting === true;
  const showOps = opsEnabled && Boolean(form.order?.hasRouting);
  const editable = form.mode !== 'readonly';
  const busy = form.pending || form.loading;
  const excludeIds = form.picker === 'input'
    ? [...form.inputs.map((row) => row.productId), form.product?.productId].filter((value): value is string => Boolean(value))
    : [...form.outputs.map((row) => row.productId), form.product?.productId].filter((value): value is string => Boolean(value));
  const outputWarehouseName = form.warehouses.find((warehouse) => warehouse.id === form.outputWarehouseId)?.name
    || form.order?.outputWarehouseName;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={form.requestLeave}>{t('orders.title')}</Button>
        <GuideButton guideKey="production.orderEditor" />
        <Box sx={{ flex: 1 }} />
        {editable ? (
          <Button variant="outlined" disabled={busy || !form.product} onClick={form.calculate}>{t('editor.calculate')}</Button>
        ) : null}
        {form.mode === 'new' && perms.canCreate && routingOn ? (
          <>
            <Button variant="outlined" disabled={busy} onClick={form.saveDraft}>{t('editor.saveDraft')}</Button>
            <Button variant="contained" disabled={busy} onClick={form.release}>{t('ops.release')}</Button>
          </>
        ) : null}
        {form.mode === 'new' && perms.canCreate && !routingOn ? (
          <>
            <Button variant="outlined" disabled={busy} onClick={form.saveDraft}>{t('editor.saveDraft')}</Button>
            {perms.canComplete ? (
              <Button variant="contained" disabled={busy} onClick={form.saveAndComplete}>{t('editor.saveAndComplete')}</Button>
            ) : null}
          </>
        ) : null}
        {form.mode === 'draft' && routingOn ? (
          <>
            {perms.canCreate ? (
              <Button variant="outlined" disabled={busy} onClick={form.save}>{t('editor.save')}</Button>
            ) : null}
            {perms.canCreate ? (
              <Button variant="contained" disabled={busy} onClick={form.release}>{t('ops.release')}</Button>
            ) : null}
            {perms.canCreate ? (
              <Button variant="outlined" color="error" disabled={busy} onClick={form.requestDelete}>{t('editor.delete')}</Button>
            ) : null}
          </>
        ) : null}
        {form.mode === 'draft' && !routingOn ? (
          <>
            {perms.canCreate ? (
              <Button variant="outlined" disabled={busy} onClick={form.save}>{t('editor.save')}</Button>
            ) : null}
            {perms.canComplete ? (
              <Button variant="contained" disabled={busy} onClick={form.openComplete}>{t('editor.complete')}</Button>
            ) : null}
            {perms.canComplete && (form.order?.descendantDraftCount ?? 0) > 0 ? (
              <Button variant="contained" color="secondary" disabled={busy} onClick={form.openComplete}>{t('editor.completeTree')}</Button>
            ) : null}
            {perms.canCreate ? (
              <Button variant="outlined" disabled={busy} onClick={form.regenerate}>{t('editor.regenerate')}</Button>
            ) : null}
            {perms.canCreate ? (
              <Button variant="outlined" color="error" disabled={busy} onClick={form.requestDelete}>{t('editor.delete')}</Button>
            ) : null}
          </>
        ) : null}
        {form.order?.hasRouting && form.order.status === 'RELEASED' ? (
          <>
            {perms.canCreate ? (
              <Button variant="outlined" disabled={busy} onClick={form.unrelease}>{t('ops.unrelease')}</Button>
            ) : null}
            {perms.canCancel ? (
              <Button variant="outlined" color="error" disabled={busy} onClick={form.openCancel}>{t('editor.cancelOrder')}</Button>
            ) : null}
          </>
        ) : null}
        {form.order?.hasRouting && form.order.status === 'IN_PROGRESS' ? (
          <>
            {perms.canComplete ? (
              <Button variant="contained" disabled={busy || !opsCanComplete} onClick={() => setOpsCompleteOpen(true)}>
                {t('ops.completeOperations')}
              </Button>
            ) : null}
            {perms.canCancel ? (
              <Button variant="outlined" color="error" disabled={busy} onClick={form.openCancel}>{t('editor.cancelOrder')}</Button>
            ) : null}
          </>
        ) : null}
        {form.order?.status === 'COMPLETED' && perms.canCancel ? (
          <Tooltip title={form.parentCompleted ? t('cancel.parentFirst') : ''}>
            <span>
              <Button variant="outlined" color="error" disabled={busy || form.parentCompleted} onClick={form.openCancel}>
                {t('editor.cancelOrder')}
              </Button>
            </span>
          </Tooltip>
        ) : null}
        {form.order ? (
          <Button variant="text" disabled={busy} onClick={() => form.setHistoryOpen(true)}>{t('editor.history')}</Button>
        ) : null}
      </Stack>

      {form.loading ? <CircularProgress sx={{ mb: 2 }} /> : null}
      {form.banner ? (
        <Alert severity={form.banner.severity} sx={{ mb: 2 }} onClose={form.clearBanner}>
          {form.banner.message}
          {form.banner.detail ? <Typography variant="body2">{form.banner.detail}</Typography> : null}
        </Alert>
      ) : null}
      {form.currencyWarning && editable ? (
        <Alert severity="warning" sx={{ mb: 2 }}>{t('editor.currencyWarning')}</Alert>
      ) : null}

      {form.order ? <ProductionOrderDetailCard order={form.order} onOpenOrder={nav.openOrder} /> : null}
      {form.order?.outputRangeWarning ? (
        <Alert severity="warning" sx={{ mt: 1 }}>{form.order.outputRangeWarning}</Alert>
      ) : null}
      {form.order && (form.order.status === 'COMPLETED' || form.order.status === 'CANCELLED') ? (
        <Box sx={{ mb: 2 }}>
          <ProductionCostSummary
            standardUnitCost={form.order.standardUnitCost}
            standardTotalCost={form.order.standardTotalCost}
            actualUnitCost={form.order.actualUnitCost}
            actualTotalCost={form.order.actualTotalCost}
            mainAllocatedCost={form.order.mainAllocatedCost}
            standardMaterialCost={form.order.standardMaterialCost}
            standardConversionCost={form.order.standardConversionCost}
            standardOverheadCost={form.order.standardOverheadCost}
            actualMaterialCost={form.order.actualMaterialCost}
            actualMachineCost={form.order.actualMachineCost}
            actualLaborCost={form.order.actualLaborCost}
            actualOverheadCost={form.order.actualOverheadCost}
            hasOutputs={form.order.outputs.length > 1}
            costWarning={form.order.costWarning}
          />
        </Box>
      ) : null}

      {editable ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <ProductionHeaderForm
            disabled={busy || !perms.canCreate}
            product={form.product}
            onProductChange={form.setProduct}
            plannedQuantity={form.plannedQuantity}
            onPlannedQuantityChange={form.setPlannedQuantity}
            quantityMode={form.quantityMode}
            onQuantityModeChange={form.setQuantityMode}
            baseQuantity={form.baseQuantity}
            onBaseQuantityChange={form.setBaseQuantity}
            warehouses={form.warehouses}
            inputWarehouseId={form.inputWarehouseId}
            onInputWarehouseChange={form.setInputWarehouse}
            outputWarehouseId={form.outputWarehouseId}
            onOutputWarehouseChange={form.setOutputWarehouse}
            separateOutput={form.separateOutput}
            onSeparateOutputChange={form.setSeparateOutput}
            productionDate={form.productionDate}
            onProductionDateChange={form.setProductionDate}
            notes={form.notes}
            onNotesChange={form.setNotes}
            initialProductId={id ? null : nav.newParams?.productId}
            onInitialProduct={form.applyInitialProduct}
          />
        </Paper>
      ) : null}

      {opsEnabled && editable && form.routingOffered ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <FormControlLabel
            control={(
              <Switch
                checked={form.useRouting === true}
                onChange={(_, checked) => form.setUseRouting(checked)}
                disabled={busy || !perms.canCreate}
              />
            )}
            label={t('ops.useRouting', { count: form.routingStepCount })}
          />
          {form.useRouting === true && form.previewSteps.length > 0 ? (
            <Accordion defaultExpanded disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>{t('ops.stepsSummary')}</AccordionSummary>
              <AccordionDetails>
                {form.previewSteps.map((step) => (
                  <Typography key={step.stepNo} variant="body2">
                    {step.stepNo} · {step.name} · {step.workCenterName || '—'} · {Math.round(step.plannedMinutes)}
                  </Typography>
                ))}
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('ops.durationTotal', {
                    hours: Math.floor(form.previewSteps.reduce((sum, step) => sum + step.plannedMinutes, 0) / 60),
                    minutes: Math.round(form.previewSteps.reduce((sum, step) => sum + step.plannedMinutes, 0) % 60),
                  })}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ) : null}
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Tabs
          value={(tab === 'operations' && showOps) || (tab === 'children' && showChildren) || (tab === 'movements' && showMovements) || (tab === 'lots' && lotsEnabled && form.order) || tab === 'inputs' || tab === 'outputs' ? tab : 'inputs'}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
        >
          {showOps ? <Tab value="operations" label={t('ops.tabTitle')} /> : null}
          <Tab value="inputs" label={t('detail.tabs.inputs')} />
          <Tab value="outputs" label={t('detail.tabs.outputs')} />
          {showChildren ? <Tab value="children" label={t('detail.tabs.children')} /> : null}
          {showMovements ? <Tab value="movements" label={t('detail.tabs.movements')} /> : null}
          {lotsEnabled && form.order ? <Tab value="lots" label={t('lots.tab')} /> : null}
        </Tabs>
        <Box sx={{ pt: 2 }}>
          {showOps && tab === 'operations' && form.order ? (
            <OrderStepsPanel
              orderId={form.order.id}
              orderStatus={form.order.status}
              productName={form.order.productName}
              stockCode={form.order.stockCode}
              unit={form.order.unit}
              outputWarehouseName={outputWarehouseName}
              onCanComplete={(canComplete, produced) => {
                setOpsCanComplete(canComplete);
                setOpsProduced(produced);
              }}
              onOrderChanged={form.refreshOrder}
            />
          ) : null}
          {tab === 'inputs' || (!showChildren && tab === 'children') || (!showMovements && tab === 'movements') ? (
            <ProductionInputsTable
              rows={form.inputs}
              warehouses={form.warehouses}
              steps={routingOn ? form.previewSteps : undefined}
              onStepChange={form.setInputStep}
              disabled={!editable || busy || !perms.canCreate}
              plannedQuantity={form.plannedQuantity}
              standardUnitCost={form.standardUnitCost}
              onActualChange={form.setInputActual}
              onUnitChange={form.setInputUnit}
              onWarehouseChange={form.setInputWarehouseRow}
              onDelete={form.removeInput}
              onAdd={() => form.openPicker('input')}
              childPlan={form.childPlan}
              childPlanWarnings={form.childPlanWarnings}
              showCreateChildren={perms.canCreate && form.mode === 'new'}
              createChildOrders={form.createChildOrders}
              onCreateChildOrdersChange={form.setCreateChildOrders}
              showCreateChildrenButton={perms.canCreate && form.mode === 'draft' && children.length === 0 && form.childPlan.length > 0}
              onCreateChildren={perms.canCreate ? form.regenerate : undefined}
              onLotChange={form.setInputLots}
            />
          ) : null}
          {tab === 'outputs' ? (
            <ProductionOutputsTable
              main={form.product || form.order ? {
                name: form.product?.name || form.order?.productName || '',
                stockCode: form.product?.stockCode || form.order?.stockCode,
                quantity: form.order?.status === 'COMPLETED'
                  ? (form.order.producedQuantity ?? form.plannedQuantity)
                  : form.plannedQuantity,
                unit: form.product?.unit || form.order?.unit,
                warehouseName: outputWarehouseName,
                unitCost: form.order?.actualUnitCost,
                allocatedCost: form.order?.mainAllocatedCost,
                trackLots: form.mainLot.trackLots,
                trackExpiry: form.mainLot.trackExpiry,
                lotNo: form.mainLot.lotNo,
                expiryDate: form.mainLot.expiryDate,
              } : null}
              rows={form.outputs}
              warehouses={form.warehouses}
              disabled={!editable || busy || !perms.canCreate}
              showUnitCost={form.order?.status === 'COMPLETED' || form.order?.status === 'CANCELLED'}
              onQuantityChange={form.setOutputQuantity}
              onWarehouseChange={form.setOutputWarehouseRow}
              onCostChange={form.setOutputCost}
              onDelete={form.removeOutput}
              onAddCoproduct={() => form.openPicker('COPRODUCT')}
              onAddByproduct={() => form.openPicker('BYPRODUCT')}
              onAddScrap={() => form.openPicker('SCRAP')}
              onMainLotChange={(patch) => {
                if (patch.lotNo !== undefined) form.setMainLotNo(patch.lotNo);
                if (patch.expiryDate !== undefined) form.setMainExpiry(patch.expiryDate);
              }}
              onOutputLotChange={form.setOutputLot}
            />
          ) : null}
          {tab === 'children' && showChildren ? (
            <ProductionChildOrdersPanel
              rows={children}
              outdated={form.order?.childrenMayBeOutdated}
              onOpen={nav.openOrder}
              onRegenerate={perms.canCreate && form.mode === 'draft' ? form.regenerate : undefined}
              regenerating={form.pending}
            />
          ) : null}
          {tab === 'movements' && showMovements ? (
            <ProductionMovementsTable rows={form.movements} loading={form.movementsLoading} />
          ) : null}
          {tab === 'lots' && lotsEnabled && form.order ? (
            <ProductionLotsPanel orderId={form.order.id} />
          ) : null}
        </Box>
      </Paper>

      <InventoryProductSelectionModal
        open={form.picker != null}
        onClose={form.closePicker}
        title={form.picker === 'SCRAP' ? t('editor.scrapTitle') : form.picker === 'BYPRODUCT' ? t('editor.byproductTitle') : form.picker === 'COPRODUCT' ? t('outputs.coProduct') : t('editor.materialTitle')}
        warehouses={form.modalWarehouses}
        categories={form.categories}
        excludeItemIds={excludeIds}
        excludeMenuItems
        onSelectProducts={(items) => {
          if (form.picker === 'input') form.addInputs(items);
          else if (form.picker === 'COPRODUCT' || form.picker === 'BYPRODUCT' || form.picker === 'SCRAP') form.addOutputs(form.picker, items);
          form.closePicker();
        }}
      />

      <CompleteProductionDialog
        open={form.completeOpen}
        plannedQuantity={completePlanned}
        variableOutput={Boolean(form.product?.isVariableOutput ?? form.order?.isVariableOutput)}
        expectedRange={completeRange}
        unit={form.order?.unit ?? form.product?.unit}
        productionDate={form.productionDate}
        rows={form.inputs}
        outputs={form.outputs}
        mainLot={form.mainLot}
        descendantDraftCount={form.order?.descendantDraftCount ?? 0}
        busy={form.pending}
        onClose={form.closeComplete}
        onConfirm={form.submitComplete}
      />
      <CancelProductionDialog
        open={form.cancelOpen}
        allowTree={form.allowCancelTree}
        busy={form.pending}
        notice={form.order?.status === 'IN_PROGRESS' ? t('ops.cancelInProgressInfo') : undefined}
        onClose={form.closeCancel}
        onConfirm={form.submitCancel}
      />
      {form.order ? (
        <CompleteOperationsDialog
          open={opsCompleteOpen}
          orderId={form.order.id}
          producedQuantity={opsProduced}
          productName={form.order.productName}
          stockCode={form.order.stockCode}
          unit={form.order.unit}
          warehouseName={outputWarehouseName}
          onClose={() => setOpsCompleteOpen(false)}
          onDone={() => {
            form.refreshOrder();
            setOpsCompleteOpen(false);
          }}
        />
      ) : null}
      <ShortageDialog
        open={form.shortageOpen}
        rows={form.shortages}
        detail={form.shortageDetail}
        showCreateChildren={form.mode === 'draft'}
        busy={form.pending}
        onClose={form.closeShortage}
        onCreateChildren={form.regenerate}
      />
      {form.order ? (
        <RecordHistoryModal
          open={form.historyOpen}
          onClose={() => form.setHistoryOpen(false)}
          entityType="ProductionOrder"
          entityId={form.order.id}
          recordLabel={form.order.orderNumber}
          entityTypeLabels={{
            ProductionOrder: t('detail.historyOrder'),
            ProductionOrderInput: t('detail.historyInput'),
            ProductionOrderOutput: t('detail.historyOutput'),
          }}
        />
      ) : null}

      <Dialog open={form.leaveOpen} onClose={form.stay}>
        <DialogTitle>{t('editor.dirtyTitle')}</DialogTitle>
        <DialogContent>{t('editor.dirtyBody')}</DialogContent>
        <DialogActions>
          <Button onClick={form.stay}>{t('editor.stay')}</Button>
          <Button color="error" onClick={form.confirmLeave}>{t('editor.dirtyLeave')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={form.recalcOpen} onClose={form.rejectRecalc}>
        <DialogTitle>{t('editor.recalcTitle')}</DialogTitle>
        <DialogContent>{t('editor.recalcBody')}</DialogContent>
        <DialogActions>
          <Button onClick={form.rejectRecalc}>{t('editor.stay')}</Button>
          <Button variant="contained" onClick={form.acceptRecalc}>{t('editor.recalcConfirm')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={form.deleteOpen} onClose={form.closeDelete}>
        <DialogTitle>{t('editor.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('editor.deleteBody')}</Typography>
          {(form.order?.descendantDraftCount ?? 0) > 0 ? (
            <Typography>{t('editor.deleteWithChildren', { count: form.order?.descendantDraftCount ?? 0 })}</Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={form.closeDelete} disabled={form.pending}>{t('editor.stay')}</Button>
          <Button color="error" variant="contained" disabled={form.pending} onClick={form.confirmDelete}>{t('editor.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(form.snack)} autoHideDuration={4000} onClose={form.clearSnack} message={form.snack} />
    </Box>
  );
};

export default ProductionOrderEditor;
